/**
 * Sidebar de navegación — fijo a la izquierda.
 *
 * Soporta items planos y grupos con submenú colapsable (chevron).
 * Los grupos recuerdan su estado expandido/colapsado en localStorage,
 * y se expanden automáticamente cuando una de sus sub-rutas está activa.
 *
 * El bloque inferior muestra el usuario activo + botón de logout visible.
 */

import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  LogOut,
  LayoutDashboard,
  Truck,
  Inbox,
  ShieldCheck,
  Activity,
  Database,
  ListTree,
  ScanSearch,
  GitBranch,
  Shield,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSessionStore } from '@/stores/session';
import { useUbicaciones } from '@/api/queries';
import { puedeActuar } from '@/domain/rbac';
import { performLogout } from '@/features/auth/logout-flow';

type IconCmp = React.ComponentType<{ className?: string }>;

interface NavLeaf {
  kind: 'leaf';
  to: string;
  label: string;
  icon: IconCmp;
  /** Solo visible si el usuario activo puede `accion`. */
  requiereAccion?: string;
  /** Marca visual para features previstas para una versión futura (no MVP). */
  versionFutura?: 'v2';
}

interface NavGroup {
  kind: 'group';
  id: string;
  label: string;
  icon: IconCmp;
  /** Si está seteado, el grupo entero solo es visible si el usuario tiene esta acción. */
  requiereAccion?: string;
  children: NavLeaf[];
}

type NavItem = NavLeaf | NavGroup;

const NAV_ITEMS: NavItem[] = [
  { kind: 'leaf', to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { kind: 'leaf', to: '/despacho', label: 'Despacho', icon: Truck },
  { kind: 'leaf', to: '/recepcion', label: 'Recepción', icon: Inbox },
  { kind: 'leaf', to: '/trazabilidad', label: 'Trazabilidad', icon: GitBranch, requiereAccion: 'ver_trazabilidad' },
  { kind: 'leaf', to: '/consulta-gln', label: 'Consulta GLN ANMAT', icon: ScanSearch },
  {
    kind: 'group',
    id: 'maestros',
    label: 'Maestros',
    icon: Database,
    children: [
      { kind: 'leaf', to: '/maestros/productos', label: 'Productos', icon: Database },
      { kind: 'leaf', to: '/maestros/clientes', label: 'Clientes', icon: Database },
      { kind: 'leaf', to: '/maestros/eventos', label: 'Eventos ANMAT', icon: Database },
    ],
  },
  {
    kind: 'group',
    id: 'seguridad',
    label: 'Seguridad',
    icon: Shield,
    requiereAccion: 'ver_seguridad',
    children: [
      { kind: 'leaf', to: '/seguridad/usuarios', label: 'Usuarios', icon: Shield },
      { kind: 'leaf', to: '/seguridad/roles', label: 'Roles y permisos', icon: Shield },
      { kind: 'leaf', to: '/seguridad/sesiones', label: 'Sesiones activas', icon: Shield },
      { kind: 'leaf', to: '/seguridad/politica', label: 'Política de sesión', icon: Shield },
    ],
  },
  { kind: 'leaf', to: '/anmat', label: 'Log ANMAT', icon: ListTree },
  { kind: 'leaf', to: '/apilog', label: 'Logs API', icon: Activity, requiereAccion: 'ver_audit_trail' },
  { kind: 'leaf', to: '/audit', label: 'Audit Trail', icon: ShieldCheck, requiereAccion: 'ver_audit_trail' },
  // Manual oculto por pedido — la ruta /manual sigue existiendo (accesible por URL directa).
  // { kind: 'leaf', to: '/manual', label: 'Manual', icon: BookOpen },
];

const STORAGE_KEY = 'riga-traza-sidebar-expanded';

function loadExpanded(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, boolean>;
  } catch {
    return {};
  }
}

export function Sidebar() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const usuario = useSessionStore((s) => s.usuario);
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const { data: ubicaciones = [] } = useUbicaciones();
  const ubicacion = ubicaciones.find((u) => u.id === ubicacionActivaId);

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => loadExpanded());

  // Auto-expandir el grupo que contiene la ruta activa
  useEffect(() => {
    const path = location.pathname;
    const groupActivo = NAV_ITEMS.find(
      (i): i is NavGroup =>
        i.kind === 'group' && i.children.some((c) => path.startsWith(c.to)),
    );
    if (groupActivo && !expanded[groupActivo.id]) {
      setExpanded((e) => ({ ...e, [groupActivo.id]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Persistir cambios
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(expanded));
  }, [expanded]);

  const toggleGroup = (id: string) => {
    setExpanded((e) => ({ ...e, [id]: !e[id] }));
  };

  const handleLogout = () => performLogout({ navigate, queryClient });

  const items = NAV_ITEMS.filter((i) => {
    if (i.kind === 'leaf') {
      return !i.requiereAccion || puedeActuar(usuario, i.requiereAccion);
    }
    // grupo: requiereAccion del grupo OR cualquiera de los hijos visible
    if (i.requiereAccion && !puedeActuar(usuario, i.requiereAccion)) return false;
    return i.children.some(
      (c) => !c.requiereAccion || puedeActuar(usuario, c.requiereAccion),
    );
  });

  return (
    <aside className="flex w-64 flex-col bg-everex-azul text-white">
      {/* Header del sidebar — logo oficial Everex (variante blanca para
          contraste sobre el fondo azul institucional). El eyebrow "Riga Traza"
          va separado del logo con `mt-3` para no chocar con el descender de
          "PHARMACEUTICALS". */}
      <div className="flex flex-col items-start border-b border-everex-azul-dark p-4">
        <img
          src="/logo-everex-blanco.svg"
          alt="Everex Pharmaceuticals"
          className="h-7 w-auto"
        />
        <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-everex-celeste">
          Riga Traza
        </div>
      </div>

      {/* Navigation — sidebar-scroll = scrollbar overlay (no toma espacio, fade-in al hover) */}
      <nav className="sidebar-scroll flex-1 overflow-y-auto p-2">
        <ul className="space-y-0.5">
          {items.map((item) =>
            item.kind === 'leaf' ? (
              <li key={item.to}>
                <SidebarLink
                  to={item.to}
                  label={item.label}
                  Icon={item.icon}
                  versionFutura={item.versionFutura}
                />
              </li>
            ) : (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => toggleGroup(item.id)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded px-3 py-1.5 text-sm transition',
                    'text-everex-celeste hover:bg-everex-azul-dark hover:text-white',
                  )}
                  aria-expanded={!!expanded[item.id]}
                  aria-controls={`sidebar-group-${item.id}`}
                >
                  <item.icon className="h-4 w-4" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {expanded[item.id] ? (
                    <ChevronDown className="h-3 w-3 opacity-70" />
                  ) : (
                    <ChevronRight className="h-3 w-3 opacity-70" />
                  )}
                </button>
                {expanded[item.id] && (
                  <ul id={`sidebar-group-${item.id}`} className="ml-3 mt-0.5 space-y-0">
                    {item.children
                      .filter(
                        (c) => !c.requiereAccion || puedeActuar(usuario, c.requiereAccion),
                      )
                      .map((c) => (
                        <li key={c.to}>
                          <SidebarSubLink to={c.to} label={c.label} />
                        </li>
                      ))}
                  </ul>
                )}
              </li>
            ),
          )}
        </ul>
      </nav>

      {/* Footer del sidebar — usuario + logout */}
      <div className="border-t border-everex-azul-dark p-4 text-xs">
        <div className="mb-2 min-w-0">
          <div className="truncate font-semibold text-white">{usuario?.nombre ?? '—'}</div>
          <div className="truncate text-everex-celeste">
            {usuario ? `${usuario.rol} · ${ubicacion?.nombre ?? '—'}` : '—'}
          </div>
        </div>
        <button
          onClick={() => void handleLogout()}
          className="flex w-full items-center justify-center gap-2 rounded border border-everex-azul-dark py-1.5 text-everex-celeste transition hover:border-everex-celeste hover:bg-everex-azul-dark hover:text-white"
          title="Cerrar sesión"
        >
          <LogOut className="h-4 w-4" />
          <span className="font-semibold">Cerrar sesión</span>
        </button>
        <div className="mt-2 flex items-center justify-between border-t border-everex-azul-dark pt-2 text-[11px] opacity-70">
          <span>
            <span className="mr-1 inline-block h-2 w-2 rounded-full bg-green-400" />
            Everex Traza
          </span>
          {/* Versión del sistema — inyectada por Vite (define __APP_VERSION__).
              Útil para soporte: el usuario lee la versión y la reporta. */}
          <span className="font-mono">v{__APP_VERSION__}</span>
        </div>
      </div>
    </aside>
  );
}

function SidebarLink({
  to,
  label,
  Icon,
  versionFutura,
}: {
  to: string;
  label: string;
  Icon: IconCmp;
  versionFutura?: 'v2';
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded px-3 py-1.5 text-sm transition',
          isActive
            ? 'bg-everex-naranja text-white'
            : 'text-everex-celeste hover:bg-everex-azul-dark hover:text-white',
        )
      }
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1">{label}</span>
      {versionFutura && (
        <span
          aria-hidden="true"
          className="rounded bg-amber-500/30 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-200"
          title="Feature prevista para una versión futura (post-MVP)"
        >
          {versionFutura}
        </span>
      )}
    </NavLink>
  );
}

function SidebarSubLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 rounded px-3 py-1 text-xs transition',
          isActive
            ? 'bg-everex-naranja/80 text-white'
            : 'text-everex-celeste/80 hover:bg-everex-azul-dark hover:text-white',
        )
      }
    >
      <span className="h-1 w-1 rounded-full bg-current opacity-60" />
      {label}
    </NavLink>
  );
}
